import { ExtendedAxiosRequestConfig } from "@shared/services/api/abstracts/Repository";
import { useModelService } from "@shared/services/api/hooks/useModelService";
import { useMemo } from "react";
import { QueryKey, UseQueryOptions } from "react-query";
import VenueBasedSquareRepository from "../data/repositories/VenueBasedSquareRepository";
import {
  VenueBasedSquareOAuthLocationSerializer,
  VenueBasedSquareOAuthTerminalSerializer,
} from "../data/types/square";

const useSquareService = (venueId: number | undefined) => {
  const repository = useMemo(
    () => new VenueBasedSquareRepository({ venueId }),
    [venueId]
  );

  const {
    useList,
    useCreate,
    useRetrieve,
    useUpdate,
    useGenericQuery,
    useGenericMutation,
    invalidateSubcategoryQueries,
    invalidateAllRepositoryQueries,
  } = useModelService(repository);

  const useListLocations = ({
    params,
    config,
    options,
  }: {
    params?: Record<string, unknown>;
    config?: ExtendedAxiosRequestConfig;
    options?: UseQueryOptions<Array<VenueBasedSquareOAuthLocationSerializer>>;
  } = {}) => {
    return useGenericQuery<Array<VenueBasedSquareOAuthLocationSerializer>>(
      "getLocationsList",
      { params, config, options }
    );
  };

  const useGetLocationInfo = () => {
    return useGenericQuery<VenueBasedSquareOAuthLocationSerializer>(
      "getLocationInfo"
    );
  };

  const useRevokeAccess = (options?: UseQueryOptions) => {
    return useGenericQuery("revokeAccess", { options });
  };

  const useGetPairedDeviceCode = (
    config: UseQueryOptions<
      VenueBasedSquareOAuthTerminalSerializer | null,
      unknown,
      VenueBasedSquareOAuthTerminalSerializer | null,
      QueryKey
    > = {}
  ) => {
    const options: UseQueryOptions<
      VenueBasedSquareOAuthTerminalSerializer | null,
      unknown,
      VenueBasedSquareOAuthTerminalSerializer | null,
      QueryKey
    > = {
      select: (data: VenueBasedSquareOAuthTerminalSerializer | null) => {
        return data && data.id ? data : null;
      },
      ...config,
    };

    return useGenericQuery<VenueBasedSquareOAuthTerminalSerializer | null>(
      "getPairedDeviceCode",
      {
        options: {
          // Prevent unauthorized toast errors from popping up on desktop login page.
          // This is an incomplete solution, as venueId is not meant to be a measure of authentication.
          enabled: Boolean(venueId),
          ...options,
        },
      }
    );
  };

  const useGenerateDeviceCode = () => {
    return useGenericMutation<
      VenueBasedSquareOAuthTerminalSerializer,
      undefined
    >("generateDeviceCode", "POST");
  };

  const useUnpairDeviceCode = () => {
    return useGenericMutation("unpairDeviceCode", "POST", {
      options: {
        onSuccess() {
          invalidateSubcategoryQueries("getPairedDeviceCode");
        },
      },
    });
  };

  return {
    useList,
    useCreate,
    useRetrieve,
    useUpdate,
    useListLocations,
    useGetLocationInfo,
    useRevokeAccess,
    useGetPairedDeviceCode,
    useGenerateDeviceCode,
    useUnpairDeviceCode,
    invalidateSubcategoryQueries,
    invalidateAllRepositoryQueries,
  };
};

export default useSquareService;
